/**
 * Guided `$input` arguments editor (ADR-0045 R4-inputs) — the SHARED consumer
 * surface used wherever a host passes a `$input` map to another page:
 *  - `page_ref.params` (embed a page inline), via `PropsInspector`;
 *  - `open_overlay.context` (open a page as a modal/drawer), via `ActionChainEditor`.
 *
 * Given the target page's RESOLVED inputs (declared contract OR auto-discovered
 * `$input.<key>` usages — see `lib/pageInputs.resolvePageInputs`), it renders a
 * GUIDED FORM: one labelled row per expected input (type/description hint, a
 * required marker + a missing-required warning), each value a full
 * `ExpressionField` (literal OR a `{{ }}` JS expression over the HOST page). So
 * the operator no longer has to KNOW which params the detail page needs — the
 * form tells them. Undeclared/extra keys still in the map get a free-form
 * "Altri parametri" section (back-compat + flexibility); when the target
 * declares/uses nothing, the whole editor degrades to that free-form section.
 */
import { useState } from "react";

import { ExpressionField } from "@/components/workflows/ExpressionField";
import type { ExprPreview } from "@/lib/pagePreview";
import type { ResolvedPageInput } from "@/lib/pageInputs";

const INPUT =
  "w-full rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-ring";

type ValueMap = Record<string, string>;

function asMap(value: unknown): ValueMap {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const out: ValueMap = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) out[k] = String(v ?? "");
    return out;
  }
  return {};
}

export function PageInputArgsEditor({
  value,
  onChange,
  inputs,
  jsAmbientTypes,
  previewExpr,
  registerInsert,
}: {
  value: unknown;
  onChange: (v: ValueMap) => void;
  /** The target page's resolved inputs (declared or discovered). Empty ⇒ the
   *  guided section is hidden and only the free-form editor shows. */
  inputs: ResolvedPageInput[];
  jsAmbientTypes?: string;
  previewExpr?: (template: string) => ExprPreview | null;
  registerInsert?: (insert: ((text: string) => void) | null) => void;
}) {
  const map = asMap(value);
  const declaredNames = new Set(inputs.map((i) => i.name));
  // Extra keys present in the map but NOT part of the resolved contract.
  const extraKeys = Object.keys(map).filter((k) => !declaredNames.has(k));
  // Buffered draft rows for new free-form keys (a blank-key row isn't lost mid-type).
  const [drafts, setDrafts] = useState<[string, string][]>([]);

  const setKey = (key: string, v: string) => onChange({ ...map, [key]: v });
  const dropKey = (key: string) => {
    const next = { ...map };
    delete next[key];
    onChange(next);
  };

  const commitDrafts = (rows: [string, string][]) => {
    setDrafts(rows);
    const next: ValueMap = { ...map };
    for (const [k, v] of rows) {
      const key = k.trim();
      if (key) next[key] = v;
    }
    onChange(next);
  };

  const valueField = (key: string, placeholder: string) => (
    <ExpressionField
      value={map[key] ?? ""}
      onChange={(nv) => setKey(key, nv)}
      placeholder={placeholder}
      jsAmbientTypes={jsAmbientTypes}
      previewExpr={previewExpr}
      registerInsert={registerInsert}
    />
  );

  return (
    <div className="flex flex-col gap-2">
      {inputs.map((inp) => {
        const missing = inp.required && !(map[inp.name] ?? "").trim();
        return (
          <div
            key={inp.name}
            className="flex flex-col gap-1 rounded-md border border-border/60 bg-surface/40 p-2"
          >
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-[11px] text-foreground">{inp.name}</span>
              {inp.required && <span className="text-danger" title="Obbligatorio">*</span>}
              {inp.type && (
                <span className="rounded bg-muted px-1 text-[9px] uppercase text-muted-foreground">
                  {inp.type}
                </span>
              )}
              {!inp.declared && (
                <span
                  className="text-[9px] text-muted-foreground/70"
                  title="Rilevato dall'uso di $input nella pagina (non dichiarato)"
                >
                  rilevato
                </span>
              )}
            </div>
            {inp.description && (
              <p className="text-[10px] leading-snug text-muted-foreground/80">{inp.description}</p>
            )}
            {valueField(inp.name, inp.type === "number" ? "0 o {{ … }}" : "valore o {{ … }}")}
            {missing && (
              <p className="text-[10px] text-danger">Parametro obbligatorio: imposta un valore.</p>
            )}
          </div>
        );
      })}

      {/* Extra / undeclared keys already in the map — editable, removable. */}
      {extraKeys.map((key) => (
        <div key={key} className="flex flex-col gap-1 rounded-md border border-dashed border-border/60 p-2">
          <div className="flex items-center justify-between gap-1">
            <span className="font-mono text-[11px] text-muted-foreground">{key}</span>
            <button
              type="button"
              className="px-1 text-muted-foreground hover:text-danger"
              title="Rimuovi parametro"
              aria-label="Rimuovi parametro"
              onClick={() => dropKey(key)}
            >
              ×
            </button>
          </div>
          {valueField(key, "valore o {{ … }}")}
        </div>
      ))}

      {/* New free-form keys (the escape hatch — extra params beyond the contract). */}
      {drafts.map(([k, v], i) => (
        <div key={`d${i}`} className="flex items-center gap-1">
          <input
            className={`${INPUT} w-1/3`}
            value={k}
            placeholder="parametro extra"
            aria-label="Nome parametro extra"
            onChange={(e) => commitDrafts(drafts.map((r, j) => (j === i ? [e.target.value, r[1]] : r)))}
          />
          <input
            className={`${INPUT} flex-1`}
            value={v}
            placeholder="valore o {{ … }}"
            aria-label="Valore parametro extra"
            onChange={(e) => commitDrafts(drafts.map((r, j) => (j === i ? [r[0], e.target.value] : r)))}
          />
          <button
            type="button"
            className="shrink-0 px-1 text-muted-foreground hover:text-danger"
            onClick={() => commitDrafts(drafts.filter((_, j) => j !== i))}
          >
            ×
          </button>
        </div>
      ))}

      <button
        type="button"
        className="self-start rounded border border-border px-1.5 py-0.5 text-[10px] text-accent hover:bg-surface"
        onClick={() => setDrafts([...drafts, ["", ""]])}
      >
        + Parametro extra
      </button>
    </div>
  );
}
